import * as uiLayout from './common/uiLayout';
import { navigationContentSx } from '../config/sidebarLayout';
import NavigationShell from './NavigationShell';
import React, { useMemo, useState } from "react";


import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import SchoolIcon from "@mui/icons-material/School";

// ✅ Excel reader
import * as XLSX from "xlsx";
import { FILES_SERVER_URL } from "../config/apiUrls";



// ✅ API Endpoint الحقيقي
const UPLOAD_URL = `${FILES_SERVER_URL}/erp/grades.php`;

const UploadGrades = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const surfaces = theme.palette.surfaces || {};

  const pageBg = isDark
    ? (theme.palette.background?.default || "#0d1b15")
    : "#f8fbfa";

  const cardBg = isDark
    ? (surfaces.card || "#13251d")
    : "#ffffff";

  const sectionBg = isDark
    ? (surfaces.section || "#172b22")
    : "#fbfdfc";

  const nestedBg = isDark
    ? (surfaces.nested || "#1b3328")
    : "#ffffff";

  const textMain = isDark
    ? (theme.palette.text?.primary || "#edf8f3")
    : "#2c3e50";

  const textMuted = isDark
    ? (theme.palette.text?.secondary || "#bdd2c8")
    : "#607080";

  const borderColor = isDark ? "#67C99D" : "#9ac9b5";
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  // صلاحية فتح الصفحة أصبحت من Form_Name + User_Premision عبر PrivateRoute.

  const [file, setFile] = useState(null);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // ✅ Structure validation state
  const [structureOk, setStructureOk] = useState(false);
  const [structureInfo, setStructureInfo] = useState(null); // { subjectsCount, headers }

  // ✅ Progress
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: 0 });

  // =========================
  // Helpers (Structure Check)
  // =========================
  const normalizeHeader = (h) =>
    String(h ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " "); // unify spaces

  // ✅ يقبل: nationalid أو national id (مسافات/كيس مش فارق)
  const isNationalIdHeader = (h) => {
    const x = normalizeHeader(h);
    return x === "nationalid" || x === "national id";
  };

  const isValidNationalIdValue = (v) => {
    const s = String(v ?? "").trim();
    // يقبل أرقام فقط (10-20 رقم) - عدّل لو عندك شكل مختلف
    return /^\d{8,20}$/.test(s);
  };

  const parseGradeValue = (v) => {
    if (v === null || v === undefined || String(v).trim() === "") return null;
    const n = Number(v);
    if (Number.isNaN(n)) return null;
    return n;
  };

  const readExcelHeaders = async (excelFile) => {
    const buffer = await excelFile.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName) throw new Error("الملف لا يحتوي على أي Sheets.");

    const sheet = workbook.Sheets[firstSheetName];

    // read rows as arrays
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    const headerRow = rows?.[0] || [];

    const headers = headerRow
      .map((c) => String(c ?? "").trim())
      .filter((v) => v !== "");

    if (headers.length === 0) throw new Error("أول صف (Header) فاضي.");

    return { headers, sheetName: firstSheetName };
  };

  const validateStructure = (headers) => {
    if (!headers || headers.length < 2) {
      throw new Error(
        "لازم يكون فيه nationalid / national id + عمود واحد على الأقل بعده."
      );
    }

    if (!isNationalIdHeader(headers[0])) {
      throw new Error("أول عمود لازم يكون اسمه nationalid أو national id.");
    }

    const rest = headers.slice(1);
    const emptyCols = rest.filter((h) => String(h ?? "").trim() === "");
    if (emptyCols.length > 0) {
      throw new Error("في أعمدة فاضية بعد nationalid — من فضلك سمّي كل الأعمدة.");
    }

    return {
      subjectsCount: rest.length,
      headers,
    };
  };

  const readExcelRows = async (excelFile) => {
    const buffer = await excelFile.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    const firstSheetName = workbook.SheetNames?.[0];
    if (!firstSheetName) throw new Error("الملف لا يحتوي على أي Sheets.");

    const sheet = workbook.Sheets[firstSheetName];

    // rows as arrays (header + data)
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    if (!rows || rows.length < 2) {
      throw new Error("لا يوجد بيانات (صفوف) بعد الهيدر.");
    }

    const headerRow = rows[0].map((c) => String(c ?? "").trim());
    const headers = headerRow.filter((v) => v !== "");

    // data rows
    const dataRows = rows.slice(1);

    return { headers, dataRows };
  };

  // =========================
  // Handlers
  // =========================
  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;

    setFile(f);
    setSuccessMsg("");
    setErrorMsg("");
    setStructureOk(false);
    setStructureInfo(null);
    setProgress({ total: 0, done: 0, failed: 0 });

    if (!f) return;

    // ✅ ext check
    const okExt = [".xls", ".xlsx"].some((ext) =>
      (f.name || "").toLowerCase().endsWith(ext)
    );
    if (!okExt) {
      setErrorMsg("الملف لازم يكون Excel بصيغة .xls أو .xlsx");
      return;
    }

    try {
      const { headers } = await readExcelHeaders(f);
      const info = validateStructure(headers);

      setStructureOk(true);
      setStructureInfo(info);
    } catch (err) {
      setStructureOk(false);
      setStructureInfo(null);
      setErrorMsg(err?.message || "فشل التحقق من هيكل الملف.");
    }
  };

  const postOneStudent = async ({ national_id, grades }) => {
    console.log("📡 POST:", UPLOAD_URL);
    console.log("📦 Payload:", { national_id, grades });

    let res;

    try {
      res = await fetch(UPLOAD_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          national_id,
          grades,
        }),
      });
    } catch (networkError) {
      console.error("Network/CORS Error:", networkError);
      throw new Error(
        "تعذر الاتصال بسيرفر رفع الدرجات. افحص CORS أو اتصال الـ API."
      );
    }

    const responseText = await res.text();

    console.log("HTTP Status:", res.status);
    console.log("Raw API Response:", responseText);

    let data = {};

    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch {
      throw new Error(
        `السيرفر لم يرجع JSON صحيح. HTTP ${res.status}: ${responseText.substring(0, 300)}`
      );
    }

    if (!res.ok) {
      throw new Error(
        data?.message || `خطأ من السيرفر HTTP ${res.status}`
      );
    }

    if (data?.success === false) {
      throw new Error(
        data?.message || "السيرفر رفض رفع درجات الطالب."
      );
    }

    return data;
  };

  const handleUpload = async () => {
    console.log("🔥 handleUpload CLICKED");

    setSuccessMsg("");
    setErrorMsg("");

    if (!file) {
      setErrorMsg("من فضلك ارفع ملف الإكسل.");
      return;
    }

    if (!structureOk) {
      setErrorMsg("هيكل الملف غير صحيح. عدّل الملف قبل الرفع.");
      return;
    }

    try {
      console.log("1️⃣ بدء قراءة الملف:", file.name);

      setLoading(true);
      setProgress({ total: 0, done: 0, failed: 0 });

      const { headers, dataRows } = await readExcelRows(file);

      console.log("2️⃣ Headers:", headers);
      console.log("3️⃣ Rows:", dataRows);

      validateStructure(headers);

      const subjects = headers.slice(1);
      const rowsToSend = [];

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i] || [];
        const nationalIdRaw = row[0];

        // لو Excel رجّع الهوية كرقم بصيغة 1234567890.0
        const national_id = String(nationalIdRaw ?? "")
          .trim()
          .replace(/\.0$/, "");

        const isRowEmpty =
          national_id === "" &&
          row.slice(1).every((x) => String(x ?? "").trim() === "");

        if (isRowEmpty) continue;

        if (!isValidNationalIdValue(national_id)) {
          throw new Error(
            `صف رقم ${i + 2}: رقم الهوية غير صحيح أو فارغ (القيمة: "${national_id}")`
          );
        }

        const grades = {};

        for (let s = 0; s < subjects.length; s++) {
          const subjectName = subjects[s];
          const cell = row[s + 1];
          const grade = parseGradeValue(cell);

          if (grade !== null) {
            grades[subjectName] = grade;
          }
        }

        if (Object.keys(grades).length === 0) {
          throw new Error(
            `صف رقم ${i + 2}: لا توجد أي درجات صالحة للطالب ${national_id}`
          );
        }

        rowsToSend.push({
          national_id,
          grades,
        });
      }

      console.log("4️⃣ البيانات الجاهزة للإرسال:", rowsToSend);

      if (rowsToSend.length === 0) {
        throw new Error("لا يوجد صفوف صالحة للإرسال.");
      }

      setProgress({
        total: rowsToSend.length,
        done: 0,
        failed: 0,
      });

      let done = 0;
      let failed = 0;
      const failedStudents = [];

      for (let i = 0; i < rowsToSend.length; i++) {
        const student = rowsToSend[i];

        try {
          console.log(
            `⬆️ رفع الطالب ${i + 1}/${rowsToSend.length}`,
            student
          );

          const result = await postOneStudent(student);

          console.log("✅ API Result:", result);
          done += 1;
        } catch (e) {
          console.error(
            "❌ فشل الطالب:",
            student.national_id,
            e
          );

          failed += 1;

          failedStudents.push({
            national_id: student.national_id,
            error: e?.message || "Unknown error",
          });
        }

        setProgress({
          total: rowsToSend.length,
          done,
          failed,
        });
      }

      if (failed === 0) {
        setSuccessMsg(
          `✅ تم رفع الدرجات بنجاح لكل الطلاب (${done}).`
        );
      } else {
        console.error("Failed students:", failedStudents);

        setErrorMsg(
          `تم رفع ${done} طالب، وفشل ${failed} طالب. ` +
            `أول خطأ: ${failedStudents[0]?.national_id || ""} - ${
              failedStudents[0]?.error || ""
            }`
        );
      }
    } catch (err) {
      console.error("🔥 UPLOAD ERROR:", err);

      setErrorMsg(
        err?.message ||
          "حصل خطأ غير متوقع أثناء رفع الدرجات."
      );
    } finally {
      console.log("🏁 Upload finished");
      setLoading(false);
    }
  };

  return (
    <NavigationShell variant="standard">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: "100vh",
          bgcolor: pageBg,
          direction: "rtl",
          overflowX: "hidden",

          ...(isDark && {
            "& .MuiPaper-root": {
              backgroundImage: "none",
            },

            "& .MuiChip-root": {
              backgroundColor: "transparent !important",
              color: "#edf8f3 !important",
              border: "1px solid #67C99D !important",
              boxShadow: "none !important",
            },

            "& .MuiButton-root": {
              background: "transparent !important",
              backgroundColor: "transparent !important",
              color: "#67C99D !important",
              border: "1px solid #67C99D !important",
              boxShadow: "none !important",

              "&:hover": {
                background: "transparent !important",
                backgroundColor: "transparent !important",
                color: "#edf8f3 !important",
                borderColor: "#67C99D !important",
                boxShadow: "0 0 0 2px rgba(103,201,157,.10) !important",
                transform: "none !important",
              },

              "&.Mui-disabled": {
                background: "transparent !important",
                backgroundColor: "transparent !important",
                color: "rgba(103,201,157,.48) !important",
                borderColor: "rgba(103,201,157,.42) !important",
              },
            },

            "& .MuiAlert-root": {
              backgroundColor: "transparent !important",
              color: "#edf8f3 !important",
              border: "1px solid #67C99D !important",
              boxShadow: "none !important",
            },

            "& .MuiAlert-icon": {
              color: "#67C99D !important",
            },

            "& .MuiDivider-root": {
              borderColor: "rgba(103,201,157,.55) !important",
            },

            "& .MuiCircularProgress-root": {
              color: "#67C99D !important",
            },
          }),
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            overflowX: "hidden",
            p: {
              xs: 0.75,
              sm: 1.25,
              md: 2.5,
            },
            ...navigationContentSx,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              overflow: "hidden",
              p: { xs: 1.1, sm: 1.75, md: 2.75 },
              borderRadius: { xs: 1.5, sm: 2, md: 3 },
              bgcolor: cardBg,
              border: `1px solid ${borderColor}`,
              boxShadow: isDark
                ? "0 12px 30px rgba(3,20,13,.24)"
                : "0 10px 25px rgba(0,0,0,.06)",
            }}
          >
            {/* Header */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1.25, md: 2 }}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
              sx={{ minWidth: 0 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <SchoolIcon sx={{ color: isDark ? "#67C99D" : "#80b49e" }} />

                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: "Cairo",
                      fontWeight: 900,
                      color: textMain,
                      fontSize: { xs: "1rem", sm: "1.15rem", md: "1.35rem" },
                    }}
                  >
                    رفع الدرجات
                  </Typography>

                  <Chip
                    label="Excel"
                    size="small"
                    variant="outlined"
                    sx={{
                      ml: 1,
                      bgcolor: isDark ? "transparent" : "#e8f5ef",
                      color: isDark ? "#67C99D" : "#2c3e50",
                      border: `1px solid ${borderColor}`,
                      fontFamily: "Cairo",
                      fontWeight: 800,
                    }}
                  />
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.8,
                    fontFamily: "Cairo",
                    color: textMuted,
                    fontSize: { xs: "0.75rem", sm: "0.8rem" },
                  }}
                >
                  اختر ملف الإكسل ثم تأكد من الهيكل قبل الرفع.
                </Typography>
              </Box>

              {progress.total > 0 && (
                <Chip
                  label={`تم: ${progress.done} / ${progress.total} | فشل: ${progress.failed}`}
                  variant="outlined"
                  sx={{
                    alignSelf: { xs: "flex-start", md: "center" },
                    maxWidth: "100%",
                    bgcolor: isDark ? "transparent" : "#f4faf7",
                    color: textMain,
                    border: `1px solid ${borderColor}`,
                    fontFamily: "Cairo",
                    fontWeight: 800,
                  }}
                />
              )}
            </Stack>

            <Divider sx={{ my: { xs: 1.5, md: 2.25 }, borderColor }} />

            {/* Upload Area */}
            <Typography
              sx={{
                fontFamily: "Cairo",
                fontWeight: 900,
                mb: 1,
                color: textMain,
              }}
            >
              رفع ملف الإكسل
            </Typography>

            <Paper
              variant="outlined"
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                p: { xs: 1, sm: 1.5, md: 2 },
                borderRadius: 2,
                border: `1px dashed ${borderColor}`,
                bgcolor: sectionBg,
                backgroundImage: "none",
                overflowX: "hidden",
              }}
            >
              <Stack
                sx={uiLayout.pageHeaderSx}
                spacing={{ xs: 1.15, sm: 1.5 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <UploadFileIcon sx={{ color: isDark ? "#67C99D" : "#80b49e" }} />

                  <Typography
                    sx={{
                      fontFamily: "Cairo",
                      fontWeight: 800,
                      color: textMain,
                    }}
                  >
                    ارفع ملف Excel (.xls / .xlsx)
                  </Typography>
                </Stack>

                <Button
                  component="label"
                  variant={isDark ? "outlined" : "contained"}
                  disabled={loading}
                  startIcon={<UploadFileIcon />}
                  sx={uiLayout.withUiSx(
                    {
                      fontFamily: "Cairo",
                      fontWeight: 900,
                      borderRadius: 2,
                      minHeight: 40,
                      py: 0.8,
                      px: 2,
                      width: { xs: "100%", md: "fit-content" },
                      bgcolor: isDark ? "transparent" : "#80b49e",
                      color: isDark ? "#67C99D" : "#fff",
                      border: `1px solid ${isDark ? "#67C99D" : "#80b49e"}`,
                      boxShadow: "none",

                      "&:hover": {
                        bgcolor: isDark ? "transparent" : "#6a9a87",
                        borderColor: isDark ? "#67C99D" : "#6a9a87",
                        color: isDark ? "#edf8f3" : "#fff",
                        boxShadow: isDark
                          ? "0 0 0 2px rgba(103,201,157,.10)"
                          : "none",
                      },
                    },
                    uiLayout.buttonSx
                  )}
                >
                  اختيار ملف

                  <input
                    hidden
                    type="file"
                    accept=".xls,.xlsx"
                    onChange={handleFileChange}
                  />
                </Button>

                {file && (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{
                      fontFamily: "Cairo",
                      bgcolor: isDark ? "transparent" : undefined,
                      color: isDark ? textMain : undefined,
                      borderColor: isDark ? "#67C99D" : undefined,
                    }}
                  >
                    تم اختيار الملف: <b>{file.name}</b>
                  </Alert>
                )}

                {file && structureOk && structureInfo && (
                  <Alert
                    severity="success"
                    variant="outlined"
                    sx={{
                      fontFamily: "Cairo",
                      bgcolor: isDark ? "transparent" : undefined,
                      color: isDark ? textMain : undefined,
                      borderColor: isDark ? "#67C99D" : undefined,
                    }}
                  >
                    ✅ الاستراكتشر صحيح — عدد الأعمدة بعد الهوية:{" "}
                    <b>{structureInfo.subjectsCount}</b>
                  </Alert>
                )}

                <Divider sx={{ borderColor }} />

                <Typography
                  sx={{
                    fontFamily: "Cairo",
                    fontWeight: 900,
                    color: textMain,
                  }}
                >
                  هيكل الشيت المطلوب (Columns)
                </Typography>

                <Alert
                  severity="warning"
                  variant="outlined"
                  sx={{
                    fontFamily: "Cairo",
                    bgcolor: isDark ? "transparent" : undefined,
                    color: isDark ? textMain : undefined,
                    borderColor: isDark ? "#67C99D" : undefined,
                  }}
                >
                  لابد أول صف يكون عناوين الأعمدة، والعمود الأول يكون:
                  <b> nationalid </b> أو <b> national id </b>

                  <Box
                    sx={{
                      mt: 1,
                      p: 1.25,
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      borderRadius: 2,
                      bgcolor: isDark ? nestedBg : "#fff",
                      color: textMain,
                      border: `1px solid ${borderColor}`,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: { xs: 11, sm: 12, md: 13 },
                      direction: "ltr",
                      textAlign: "left",
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      overflowX: "hidden",
                    }}
                  >
                    national id | إدارة المستشفيات | التوجيه المهني والتميز | ...
                  </Box>
                </Alert>

                <Divider sx={{ borderColor }} />

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={{ xs: 1.25, md: 2 }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                  sx={{ minWidth: 0 }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {successMsg && (
                      <Alert
                        severity="success"
                        variant="outlined"
                        sx={{
                          fontFamily: "Cairo",
                          bgcolor: isDark ? "transparent" : undefined,
                          color: isDark ? textMain : undefined,
                          borderColor: isDark ? "#67C99D" : undefined,
                        }}
                      >
                        {successMsg}
                      </Alert>
                    )}

                    {errorMsg && (
                      <Alert
                        severity="error"
                        variant="outlined"
                        sx={{
                          fontFamily: "Cairo",
                          bgcolor: isDark ? "transparent" : undefined,
                          color: isDark ? textMain : undefined,
                          borderColor: isDark ? "#67C99D" : undefined,
                          "& .MuiAlert-icon": {
                            color: isDark ? "#67C99D" : undefined,
                          },
                        }}
                      >
                        ❌ {errorMsg}
                      </Alert>
                    )}
                  </Box>

                  <Button
                    onClick={handleUpload}
                    disabled={loading || !file || !structureOk}
                    variant={isDark ? "outlined" : "contained"}
                    startIcon={
                      loading ? (
                        <CircularProgress
                          size={18}
                          sx={{ color: isDark ? "#67C99D" : "inherit" }}
                        />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                    sx={uiLayout.withUiSx(
                      {
                        fontFamily: "Cairo",
                        fontWeight: 900,
                        borderRadius: 2,
                        minHeight: 40,
                        py: 0.8,
                        px: 2.5,
                        bgcolor: isDark ? "transparent" : "#80b49e",
                        color: isDark ? "#67C99D" : "#fff",
                        border: `1px solid ${isDark ? "#67C99D" : "#80b49e"}`,
                        boxShadow: "none",
                        whiteSpace: "nowrap",
                        minWidth: { xs: "100%", md: 220 },

                        "&:hover": {
                          bgcolor: isDark ? "transparent" : "#6a9a87",
                          borderColor: isDark ? "#67C99D" : "#6a9a87",
                          color: isDark ? "#edf8f3" : "#fff",
                          boxShadow: isDark
                            ? "0 0 0 2px rgba(103,201,157,.10)"
                            : "none",
                        },
                      },
                      uiLayout.buttonSx
                    )}
                  >
                    {loading ? "جاري الرفع..." : "رفع الدرجات"}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Paper>
        </Box>
      </Box>
    </NavigationShell>
  );
};

export default UploadGrades;
